/* eslint-disable jsx-a11y/interactive-supports-focus */
import { useCallback, useEffect, useState, forwardRef } from 'react';
import { cx } from '@linaria/core';

import { IconThinChevron } from 'components/Icons';

import { CollapsibleSectionProps } from 'components/CollapsibleSection/types';
import { container, headerContainer, caretIcon as caretIconCssClass, contentContainer } from 'components/CollapsibleSection/styles';

export const CollapsibleSection = forwardRef<HTMLDivElement, CollapsibleSectionProps>(
  (
    {
      className,
      headerContainerClassName,
      contentContainerClassName,
      caretIconClassName,
      CaretIcon = IconThinChevron,
      caretIconSize = 16,
      caretIconColor,
      header,
      caretPosition = 'left',
      children,
      isCollapsed = false,
      onCollapse,
      onExpand,
      ...rest
    },
    ref,
  ) => {
    const [collapseState, setCollapseState] = useState(isCollapsed);

    useEffect(() => {
      setCollapseState(isCollapsed);
    }, [isCollapsed]);

    const onToggleState = useCallback(() => {
      setCollapseState((currentCollapseState) => {
        const newCollapseState = !currentCollapseState;

        if (newCollapseState) {
          onCollapse?.();
        } else {
          onExpand?.();
        }

        return newCollapseState;
      });
    }, [onCollapse, onExpand]);

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
          onToggleState();
        }
      },
      [onToggleState],
    );

    return (
      <div ref={ref} className={cx(container, className)} {...rest}>
        <div
          className={cx(headerContainer, headerContainerClassName, caretPosition === 'right' && 'reverse-aligned')}
          role="button"
          tabIndex={0}
          onClick={onToggleState}
          onKeyDown={onKeyDown}
        >
          <CaretIcon
            className={cx(caretIconCssClass, caretIconClassName, collapseState && 'collapsed')}
            size={caretIconSize}
            color={caretIconColor}
          />
          {header}
        </div>
        <div className={cx(contentContainer, contentContainerClassName, 'collapsible-content', collapseState ? 'collapsed' : 'expanded')}>
          {children}
        </div>
      </div>
    );
  },
);
